import React from 'react';
import PropTypes from 'prop-types';

import {
  Center,
  Container,
  Title,
  TypographyStylesProvider,
  rem,
  useMantineTheme,
} from '@mantine/core';

import ButtonContatto from '../ButtonContatto';
import Faq from '../Faq';
import FeatureBlock from '../FeatureBlock';
import FeatureBlockCompact from '../FeatureBlockCompact';
import FeaturesConHero from '../FeaturesConHero';
import FormContatto from '../FormContatto';
import Hero from '../Hero';
import TabellaContenuti from '../TabellaContenuti';
import ButtonBlock from '../ButtonBlock';
import FeatureCompare from '../FeatureCompare';
import CardsBlock from '../CardsBlock';
import * as classes from './GenericPage.module.css';


function GenericPage({
  pageContext: {
    mostraButtonFormContatto,
    composizioneBlocchi,
    formContattiOverride,
    stub,
  },
  landingData,
  apriFormContattoFunction,
}) {
  const formContattoData = formContattiOverride || landingData.formContatti;
  const { colors } = useMantineTheme();

  return (
    <Container fluid style={{ padding: `${rem(56)} 0 0 0` }}>
      {composizioneBlocchi.map(({
        id,
        model,
        heroAbilita,
        heroSottotitolo,
        heroFeatures,
        heroButtons,
        heroTitolo,
        heroImage,
        featuresSottotitolo,
        featuresVisualizzazione,
        featuresTitolo,
        featuresList,
        featuresButtons,
        tabelleTitolo,
        tabelleButtons,
        tabelleRighe,
        faqTitolo,
        faqList,
        titoloPagina,
        contenutoTestuale,
        buttons,
        blockCompareButton,
        blockCompareTitolo,
        blockCompareSottotitolo,
        blockCompareFeatures,
        formContattiOverride: composizioneBlocchiFormContattoOverride,
        blocksCardButtons,
        blocksCardTitolo,
        blocksCardDescription,
        blocksCardCards,
        stile,
      }) => {
        return (
          <Container key={id} m={0} p={0} fluid>
            {model?.apiKey === 'blocks_blocco_hero' && (
              <Hero
                heroAbilita={heroAbilita}
                heroSottotitolo={heroSottotitolo}
                heroFeatures={heroFeatures}
                heroButtons={heroButtons}
                heroTitolo={heroTitolo}
                heroImage={heroImage}
                stile={stile}
              />
            )}

            {model?.apiKey === 'blocks_features_block' && featuresVisualizzazione?.toLowerCase() === 'classico' && (
              <FeatureBlock
                featuresSottotitolo={featuresSottotitolo}
                featuresTitolo={featuresTitolo}
                featuresList={featuresList}
                featuresButtons={featuresButtons}
                stile={stile}
              />
            )}

            {model?.apiKey === 'blocks_features_block' && featuresVisualizzazione?.toLowerCase() === 'compatto' && (
              <FeatureBlockCompact
                featuresSottotitolo={featuresSottotitolo}
                featuresTitolo={featuresTitolo}
                featuresList={featuresList}
                featuresButtons={featuresButtons}
                stile={stile}
              />
            )}

            {model?.apiKey === 'blocks_features_block' && featuresVisualizzazione?.toLowerCase() === 'conhero' && (
              <FeaturesConHero
                featuresSottotitolo={featuresSottotitolo}
                featuresTitolo={featuresTitolo}
                featuresList={featuresList}
                featuresButtons={featuresButtons}
                stile={stile}
              />
            )}

            {model?.apiKey === 'blocks_blocco_contenuto_testuale' && (
              <Container mb="xl" pt="md">
                {titoloPagina && <Title order={2} ta="center" className={classes.titleTextModule}>
                  {titoloPagina}
                </Title>
                }
                {contenutoTestuale && (
                  <TypographyStylesProvider mt="xl">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: contenutoTestuale
                      }}
                    />
                  </TypographyStylesProvider>
                )}
                {!!buttons?.length && (
                  <Center>
                    <ButtonBlock buttons={buttons} />
                  </Center>
                )}
              </Container>
            )}

            {model?.apiKey === 'blocks_blocco_form_contatti' && (
              <Container
                pt="xl"
                pb="xl"
                fluid
                style={{ backgroundColor: stile?.coloreDiSfondo ? colors[stile?.coloreDiSfondo][1] : 'inherit' }}
              >
                <FormContatto
                  formContattoData={{
                    ...(composizioneBlocchiFormContattoOverride || formContattoData),
                    stub,
                  }}
                  stile={stile}
                />
              </Container>
            )}

            {model?.apiKey === 'blocks_blocco_tabella_riassuntiva' && (
              <TabellaContenuti
                titolo={tabelleTitolo}
                buttons={tabelleButtons}
                righe={tabelleRighe}
              />
            )}

            {model?.apiKey === 'blocks_blocco_faq' && (
              <Faq
                faqList={faqList}
                faqTitolo={faqTitolo}
              />
            )}

            {model?.apiKey === 'block_compare' && (
              <FeatureCompare
                featuresSottotitolo={blockCompareSottotitolo}
                featuresTitolo={blockCompareTitolo}
                featuresList={blockCompareFeatures}
                featuresButtons={blockCompareButton}
                stile={stile}
              />
            )}

            {model?.apiKey === 'blocks_card' && (
              <CardsBlock
                buttons={blocksCardButtons}
                title={blocksCardTitolo}
                description={blocksCardDescription}
                cards={blocksCardCards}
                stile={stile}
              />
            )}
          </Container>
        )
      })}

      {mostraButtonFormContatto && (
        <ButtonContatto
          formContattiLabelCta={formContattoData.formContattiLabelCta}
          apriFormContattoFunction={apriFormContattoFunction}
        />
      )}
    </Container>
  );
}

GenericPage.propTypes = {
  pageContext: PropTypes.shape({
    composizioneBlocchi: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          model: PropTypes.shape({
            apiKey: PropTypes.string.isRequired,
          }).isRequired,
          heroSottotitolo: PropTypes.string,
          heroFeatures: PropTypes.array,
          heroButtons: PropTypes.array,
          heroTitolo: PropTypes.string,
          heroImage: PropTypes.object,
        }),
        PropTypes.shape({
          model: PropTypes.shape({
            apiKey: PropTypes.string.isRequired,
          }).isRequired,
          featuresSottotitolo: PropTypes.string,
          featuresTitolo: PropTypes.string,
          featuresList: PropTypes.array,
          featuresButtons: PropTypes.array,
        }),
        PropTypes.shape({
          model: PropTypes.shape({
            apiKey: PropTypes.string.isRequired,
          }).isRequired,
          titoloPagina: PropTypes.string,
          contenutoTestuale: PropTypes.string,
        }),
        PropTypes.shape({
          model: PropTypes.shape({
            apiKey: PropTypes.string.isRequired,
          }).isRequired,
          faqTitolo: PropTypes.string,
          faqList: PropTypes.array,
        }),
      ])
    ),
    tabelleTitolo: PropTypes.string,
    licenzeList: PropTypes.array,
    licenzeButtons: PropTypes.array,
    mostraButtonFormContatto: PropTypes.bool,
    formContattiOverride: PropTypes.shape({
      formContattiTitolo: PropTypes.string,
      formContattiTestoIntroduttivo: PropTypes.string,
      formContattiFormTesto: PropTypes.string,
      formContattiFormTitolo: PropTypes.string,
      formContattiLabelCta: PropTypes.string,
      formContattiTitleModal: PropTypes.string,
      formContattiButtons: PropTypes.array,
    }),
  }),
};

export default GenericPage;
